"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const Dialog_1 = require("office-ui-fabric-react/lib/Dialog");
const Button_1 = require("office-ui-fabric-react/lib/components/Button");
const Checkbox_1 = require("office-ui-fabric-react/lib/Checkbox");
const ZammoWebChat_1 = require("../styles/ZammoWebChat");
const Webchat = ({ zammoApiBaseUrl, scriptCssUrl, scriptSrcUrl, dockMode, handleChangeDockMode, getDirectlineToken, getSpeechSessionInfo, getAuthorizationToken, getSimulatorToken, onCloseClick, isStandAlone, shouldShowSecurityNotice, handleSecurityNoticePromptResponse, onActivityReceived, getAdditionalLaunchInformation, shouldFocusTextBoxInput, getExtensionContent, iFrameContainerId, }) => {
    const webchatRef = react_1.useRef(null);
    const [currentZammoBotUIInstance, setCurrentZammoBotUIInstance] = react_1.useState(undefined);
    const [wantsToAlwaysContinue, setWantsToAlwaysContinue] = react_1.useState(false);
    const isMounted = react_1.default.useRef(true);
    const removeExistingBotUIComponents = () => {
        const existingUIKitComponents = document.querySelectorAll('#zammoBotUIComponent');
        const existingStyleElement = document.querySelector('#zammoBotUIKitCustomExtensionStyle');
        const existingScriptElement = document.querySelector('#zammoBotUIKitCustomExtensionScript');
        if (existingStyleElement) {
            existingStyleElement.remove();
        }
        if (existingScriptElement) {
            existingScriptElement.remove();
        }
        if (existingUIKitComponents) {
            for (const existingUIKitComponent of existingUIKitComponents) {
                existingUIKitComponent.remove();
            }
        }
    };
    react_1.useEffect(() => {
        return () => {
            isMounted.current = false;
            removeExistingBotUIComponents();
        };
    }, []);
    /**
     * In single page application, the webchat can have difficulty to claim the focus on the input.
     *
     * This approach uses a React driven focus set which overrides any competing system on page.
     */
    const focusTextBoxInput = (delay = 0) => {
        setTimeout(() => {
            var _a;
            const element = ((_a = webchatRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('input.msger-input')) || null;
            if (element) {
                element.focus();
            }
        }, delay);
    };
    /**
     * This method will override the default UI Kit system to retrieve a Directline token and setup
     * the bot conversation.
     * The overrides handles Simulator and Zammo Business (in portal and UI builder test chat bot)use cases
     * @param {ZammoBotUI} zammoBotUIInstance a valid ZammoBotUI instance.
     */
    const connectUIKit = async (zammoBotUIInstance) => {
        zammoBotUIInstance.appendTyping();
        zammoBotUIInstance.disableUserInput();
        const token = await getDirectlineToken(zammoBotUIInstance.user.id);
        await new Promise((resolve) => zammoBotUIInstance.initializeDirectLineConversation(token, false, resolve));
        zammoBotUIInstance.getSpeechTokenInfo = async (cb) => {
            if (zammoBotUIInstance.speechTokenInfo &&
                zammoBotUIInstance.isValidSpeechToken(zammoBotUIInstance.speechTokenInfo)) {
                cb({
                    isSuccess: true,
                    token: zammoBotUIInstance.speechTokenInfo.token,
                    region: zammoBotUIInstance.speechTokenInfo.region,
                });
                return;
            }
            const speechSession = await getSpeechSessionInfo();
            zammoBotUIInstance.processSpeechTokenInfo(speechSession.token, speechSession.region, cb);
        };
        const launchActivity = zammoBotUIInstance.getLaunchActivity();
        if (getAuthorizationToken) {
            const userAuthorizationToken = await getAuthorizationToken();
            const userAuthorizationEntity = {
                type: 'AuthorizationToken',
                token: userAuthorizationToken,
            };
            launchActivity.entities.push(userAuthorizationEntity);
        }
        if (getSimulatorToken) {
            const simulatorToken = await getSimulatorToken();
            const simulatorEntity = {
                type: 'SimulatorConfig',
                value: {
                    token: simulatorToken,
                    isChat: true,
                    isTryZammoWebchat: false,
                },
            };
            launchActivity.entities.push(simulatorEntity);
        }
        if (getAdditionalLaunchInformation) {
            const additionalLaunchInfo = await getAdditionalLaunchInformation();
            if (additionalLaunchInfo.entities) {
                launchActivity.entities = launchActivity.entities.concat(additionalLaunchInfo.entities);
            }
        }
        zammoBotUIInstance.sendActivity(launchActivity);
    };
    const initializeUIKitInstance = async () => {
        const checkUnmounted = () => {
            if (!isMounted.current) {
                throw new Error('WebChat is already unmounted.');
            }
        };
        removeExistingBotUIComponents();
        try {
            checkUnmounted();
            const windowContext = iFrameContainerId
                ? document.querySelector(`#${iFrameContainerId}`).contentWindow
                : window;
            const ZammoBotUIKit = windowContext.ZammoBotUIKit;
            checkUnmounted();
            const zammoBotUIInstance = ZammoBotUIKit.initialize();
            if (getExtensionContent) {
                zammoBotUIInstance.getExtensionContent = async (cb) => {
                    const extensionCssAndJs = await getExtensionContent();
                    cb(extensionCssAndJs);
                };
            }
            checkUnmounted();
            await new Promise((resolve) => zammoBotUIInstance.run(resolve));
            zammoBotUIInstance.isFirstOpening = false;
            zammoBotUIInstance.shouldIgnoreUiBuilderIgnore = true;
            // overwrite reset session to use the directline token we control
            checkUnmounted();
            zammoBotUIInstance.resetSession = async () => {
                zammoBotUIInstance.clearChatHistory();
                await connectUIKit(zammoBotUIInstance);
            };
            if (!isStandAlone) {
                // overwrite the close event to let the caller control the behavior
                // if the caller has added the optional onCloseClick
                if (onCloseClick) {
                    zammoBotUIInstance.closeChat = () => {
                        onCloseClick();
                    };
                }
                // TODO: define behavior for the simulator which uses the icon
                checkUnmounted();
                zammoBotUIInstance.openChat();
                zammoBotUIInstance.chatFrame.style.bottom = '0';
                zammoBotUIInstance.chatFrame.style.right = '0';
                zammoBotUIInstance.chatFrame.style.width = '100%';
                zammoBotUIInstance.chatFrame.style.height = '100%';
                zammoBotUIInstance.chatFrame.style.maxHeight = '100%';
                zammoBotUIInstance.chatFrame.style.maxWidth = '100%';
                const msgerSection = document.querySelector('#zammoBotUIComponent--chatFrame--chat .msger');
                if (msgerSection)
                    msgerSection.style.maxWidth = '100%';
                checkUnmounted();
                await connectUIKit(zammoBotUIInstance);
            }
            else {
                const customConnectionExtension = {
                    id: 'ZammoCustomConnectionExtension',
                    canHandleHook(hookName, zammoBotUIInstanceInExtensionContext) {
                        return hookName === 'END_OPENING_CHATBOT' && !zammoBotUIInstance.conversation;
                    },
                    async handleHook(hookName, zammoBotUIInstanceInExtensionContext) {
                        // TODO check if the bot already has a session.
                        await connectUIKit(zammoBotUIInstance);
                    },
                    canHandleOutbound() {
                        return false;
                    },
                    canHandleInbound() {
                        return false;
                    },
                    shouldContinueAfterHandlingHook(hookName) {
                        return hookName === 'END_OPENING_CHATBOT';
                    },
                };
                zammoBotUIInstance.addExtension(customConnectionExtension);
            }
            if (onActivityReceived) {
                checkUnmounted();
                const activityHandlerExtension = {
                    id: 'ZammoActivityHandlerExtension',
                    canHandleHook(hookName, zammoBotUIInstanceInExtensionContext) {
                        return hookName === 'START_PROCESSING_CHATBOT_ACTIVITY';
                    },
                    async handleHook(hookName, zammoBotUIInstanceInExtensionContext, activity) {
                        // TODO check if the bot already has a session.
                        await onActivityReceived(activity);
                    },
                    canHandleOutbound() {
                        return false;
                    },
                    canHandleInbound() {
                        return false;
                    },
                    shouldContinueAfterHandlingHook(hookName) {
                        return hookName === 'START_PROCESSING_CHATBOT_ACTIVITY';
                    },
                };
                if (!isMounted.current)
                    return;
                zammoBotUIInstance.addExtension(activityHandlerExtension);
            }
            if (!isMounted.current)
                return;
            setCurrentZammoBotUIInstance(zammoBotUIInstance);
        }
        catch (e) {
            removeExistingBotUIComponents();
            console.error(e);
        }
    };
    const initializeUIKit = async () => {
        const existingZammoScript = iFrameContainerId ? false : document.querySelector('#ZammoBotUIKitScript');
        if (!existingZammoScript) {
            const script = document.createElement('script');
            script.id = 'ZammoBotUIKitScript';
            script.src = scriptSrcUrl; // TODO: replace with dynamic URL from the backend
            script.async = true;
            script.setAttribute('cssUrl', scriptCssUrl);
            script.setAttribute('zammoApiBaseUrl', zammoApiBaseUrl);
            script.setAttribute('shouldShowMicrophone', 'true');
            script.setAttribute('shouldShowExtensionMenu', 'true');
            script.setAttribute('executionType', 'controlled');
            if (!isStandAlone) {
                script.setAttribute('loadingDivId', 'Zammo-WebChatContainer');
            }
            script.onload = async () => {
                await initializeUIKitInstance();
            };
            if (iFrameContainerId) {
                const rawframe = document.querySelector(`#${iFrameContainerId}`);
                let framedoc = rawframe.contentDocument;
                if (!framedoc && rawframe.contentWindow) {
                    framedoc = rawframe.contentWindow.document;
                }
                framedoc.body.appendChild(script);
            }
            else {
                document.body.appendChild(script);
            }
        }
        else {
            await initializeUIKitInstance();
        }
    };
    react_1.useEffect(() => {
        if (!shouldShowSecurityNotice) {
            initializeUIKit();
        }
    }, [shouldShowSecurityNotice]);
    react_1.useEffect(() => {
        if (dockMode && currentZammoBotUIInstance) {
            const dockModeAfterClickOnExtensionContent = dockMode === 'DOCK_TO_RIGHT' ? 'Make the chat widget floating' : 'Dock the chat widget';
            const dockModeAfterClickOnExtensionAriaLabel = dockMode === 'DOCK_TO_RIGHT'
                ? 'Make the chat widget floating on the screen'
                : 'Dock the chat widget to the right of the Q&amp;A table';
            currentZammoBotUIInstance.addExtensionMenuOption(dockModeAfterClickOnExtensionContent, () => {
                handleChangeDockMode(dockMode);
                focusTextBoxInput(500); // adding the delay as the widget needs to re-appear on screen
            }, 'zammo-chat-widget-change-dock-mode', dockModeAfterClickOnExtensionAriaLabel);
            if (dockMode !== 'DOCK_TO_RIGHT') {
                currentZammoBotUIInstance.showDraggableSection();
            }
            else {
                currentZammoBotUIInstance.hideDraggableSection();
            }
        }
    }, [dockMode, currentZammoBotUIInstance]);
    react_1.useEffect(() => {
        if (shouldFocusTextBoxInput && currentZammoBotUIInstance) {
            focusTextBoxInput();
        }
    }, [shouldFocusTextBoxInput, currentZammoBotUIInstance]);
    const dialogContentProps = {
        type: Dialog_1.DialogType.normal,
        title: 'Do you trust the authors?',
    };
    const shouldShowTheModal = shouldShowSecurityNotice;
    return (react_1.default.createElement(ZammoWebChat_1.Wrapper, null,
        !shouldShowTheModal && react_1.default.createElement(ZammoWebChat_1.Chat, { ref: webchatRef, id: "Zammo-WebChatContainer" }),
        shouldShowTheModal && (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(Dialog_1.Dialog, { isOpen: true, dialogContentProps: dialogContentProps },
                react_1.default.createElement("p", null, "The chat bot widget will run a custom script that was not authored by Zammo.ai"),
                react_1.default.createElement("p", null,
                    "If you know and trust the source of the script, click on ",
                    react_1.default.createElement("b", null, "Continue"),
                    " ."),
                react_1.default.createElement("p", null,
                    "If not, use the ",
                    react_1.default.createElement("b", null, "Go back"),
                    " button."),
                react_1.default.createElement(Checkbox_1.Checkbox, { label: "Always continue for this chatbot", onChange: (ev, isChecked) => setWantsToAlwaysContinue(isChecked) }),
                react_1.default.createElement(Dialog_1.DialogFooter, null,
                    react_1.default.createElement(Button_1.PrimaryButton, { onClick: () => {
                            if (handleSecurityNoticePromptResponse)
                                handleSecurityNoticePromptResponse(true, wantsToAlwaysContinue);
                        }, text: "Continue" }),
                    react_1.default.createElement(Button_1.DefaultButton, { onClick: () => {
                            if (handleSecurityNoticePromptResponse)
                                handleSecurityNoticePromptResponse(false);
                        }, text: "Go back" })))))));
};
exports.default = Webchat;
//# sourceMappingURL=ZammoWebChat.js.map